import React, { Fragment, FunctionComponent } from 'react';
import { WPSimpleArticle } from 'types/articles';
import { WPAcfGlobalBlogPost, WPAcfKnowledgeItem } from 'types/acf';
import { Statistics, DescriptionColumns, DisqusComments, Asset } from 'components/containers';
import { CustomArticleSlugTypes, RegularPostTypes } from 'types/global';
import { getArticleTerms } from 'utils';
import { usePages, usePosts } from 'hooks';
import { WPSimplePage } from 'types/pages';
import { EmbedForm, Block, BlockContent, Image, Card } from 'components/elements';
import PostItems from 'components/containers/PostItems/PostItems';
import { getArticleFeaturedImage } from 'utils';
import styles from './KnowledgeItem.module.scss';
import { WpFeaturedMediaImageTypes } from 'types/terms';
import { useRouteTranslations } from 'utils/languages';

interface OwnProps {
  data: WPSimpleArticle<WPAcfKnowledgeItem>;
}

type KnowledgeItemProps = JSX.IntrinsicElements['div'] & OwnProps;

const KnowledgeItem: FunctionComponent<KnowledgeItemProps> = props => {
  const POSTS_PER_PAGE = 3;
  const { data: knowledgeItem } = props;
  const { acf, _embedded, title, content, featured_media_src_url } = knowledgeItem || {};
  const { rendered: assetTitle } = title;
  const { rendered: assetDescription } = content;
  const { resource, descriptionColumns, statistics } = acf;
  const terms = getArticleTerms(_embedded);
  const featuredImage = getArticleFeaturedImage(_embedded);
  const { translateSlug } = useRouteTranslations();
  const sizes = featuredImage[0]?.media_details.sizes || {};
  const srcSetArray = Object.keys(sizes).map(
    size => `${sizes[size as WpFeaturedMediaImageTypes].source_url} ${sizes[size as WpFeaturedMediaImageTypes].width}w`,
  );
  const { category = [] } = terms;
  const firstCategory = category.find(category => category.name);
  const GLOBAL_PAGE_SLUG_TYPE: CustomArticleSlugTypes = 'global_blog_post';
  const { data: page } = usePages<WPSimplePage<WPAcfGlobalBlogPost>>({ slug: GLOBAL_PAGE_SLUG_TYPE });
  const [globalPageOptions] = page || [];
  const { acf: globalAcf } = globalPageOptions || {};

  const { data: relatedArticles } = usePosts({
    ...(firstCategory && { category_name: firstCategory.slug as RegularPostTypes }),
  });

  return (
    <Fragment>
      <Block id="knowledge-item" direction="row" data-module="knowledge-item">
        <BlockContent className={styles.container} direction="row">
          {resource && <Asset className={styles.column} assetTitle={assetTitle} assetDescription={assetDescription} data={resource} />}
          {resource && (
            <div {...(firstCategory && { id: `${firstCategory.slug}-form` })} key={resource?.formId} className={styles.column}>
              <Card className={styles.embedWrapper}>
                {featuredImage && (
                  <Image
                    className={styles.image}
                    sizes={`(max-width: 1023px) ${100}vw, (min-width: 1024px) and (max-width: 1365px) ${50}vw, ${50}vw`}
                    srcSet={srcSetArray.join(',')}
                    src={featured_media_src_url}
                    height={featuredImage[0]?.media_details.height}
                    width={featuredImage[0]?.media_details.width}
                  />
                )}
                <EmbedForm
                  name={firstCategory?.slug || 'knowledge-item'}
                  className={styles.embedForm}
                  message={resource?.message}
                  embed={resource?.embed}
                  theme="white"
                  formId={resource?.formId}
                />
              </Card>
            </div>
          )}
        </BlockContent>
      </Block>
      {descriptionColumns && <DescriptionColumns data={descriptionColumns} />}
      {statistics && <Statistics data={statistics} />}
      <DisqusComments article={knowledgeItem} disable={true} />
      {relatedArticles && firstCategory?.slug && (
        <PostItems
          mobile={1}
          tablet={3}
          desktop={3}
          category={translateSlug(firstCategory.slug)}
          posts={relatedArticles}
          linkLabel={globalAcf?.blogPosts?.buttonLabel}
          length={POSTS_PER_PAGE}
        />
      )}
    </Fragment>
  );
};

export default KnowledgeItem;
